import {
    FieldArray,
    FieldArrayRenderProps,
    useFormikContext,
} from 'formik';
import {
    ActionIcon,
    Alert,
    Autocomplete,
    Badge,
    Button,
    Card,
    Group,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import {
    AiOutlineArrowDown,
    AiOutlineArrowUp,
    AiOutlineDelete,
    AiOutlinePlus,
} from 'react-icons/ai';

import {useTestingActionsQuery} from '../../testing-actions/hooks/useTestingActionsQuery.ts';
import {ActionStatus} from '../../testing-actions/types.ts';
import {
    ExpectedOutcome,
    TestDefinitionFormValues,
} from '../types.ts';
import {StepInputsField} from './StepInputsField.tsx';

const EXPECTED_OUTCOME_OPTIONS = [
    {
        label: 'Expect Pass',
        value: ExpectedOutcome.EXPECT_PASS,
    },
    {
        label: 'Expect Fail',
        value: ExpectedOutcome.EXPECT_FAIL,
    },
    {
        label: 'Expect Skip',
        value: ExpectedOutcome.EXPECT_SKIP,
    },
];

// The ordered step editor.
//
// ORDER IS THE ARRAY ORDER. Moving a step swaps two entries; `stepNumber` is
// re-derived contiguously from 1 in the service on save (see `toStepsInput`),
// so a move — or a delete from the middle — can never leave a gap, a duplicate,
// or a stored order that disagrees with what the operator saw. The number shown
// on each card is therefore the POSITION, not a stored value being edited.
//
// Move up/down rather than drag-and-drop: no new dependency, and the affordance
// stays keyboard-reachable, which a drag handle would not be without extra work.
//
// ⚠️ `actionCode` is a free-text Autocomplete, NEVER a Select. Suggestions list
// ACTIVE actions purely as a convenience — an actionCode that is not in the
// catalog is LEGAL. The executor fails OPEN: an unregistered code records an
// `unimplemented` observation and the run continues. Constraining this to the
// catalog would break that seam.
export const StepsField = () => {

    const {values} = useFormikContext<TestDefinitionFormValues>();

    const {data: actions = []} = useTestingActionsQuery();

    const activeActionCodes = actions
        .filter(action => action.status === ActionStatus.ACTIVE)
        .map(action => action.actionCode);

    const steps = values.steps ?? [];

    return (
        <FieldArray name='steps'>
            {({
                  push,
                  remove,
                  swap,
                  form,
              }: FieldArrayRenderProps) => (
                <Stack gap={'sm'}>
                    <div>
                        <Text fw={500} size={'sm'}>Steps</Text>
                        <Text size={'xs'} c={'dimmed'}>
                            Steps run in the order shown. Suggestions list ACTIVE actions, but
                            any code is accepted — an unregistered code is recorded as an
                            &quot;unimplemented&quot; observation rather than failing the run.
                        </Text>
                    </div>

                    {steps.length === 0 ? (
                        <Alert color='gray' variant='light'>
                            No steps yet. A test definition with no steps does nothing when run.
                        </Alert>
                    ) : null}

                    {steps.map((step, index) => (
                        <Card key={index} withBorder padding={'sm'}>
                            <Stack gap={'sm'}>
                                <Group justify={'space-between'}>
                                    <Group gap={'xs'}>
                                        {/* The POSITION, renumbered on save. */}
                                        <Badge variant='light'>{`Step ${index + 1}`}</Badge>
                                    </Group>
                                    <Group gap={'xs'}>
                                        <ActionIcon
                                            aria-label={`Move step ${index + 1} up`}
                                            variant='subtle'
                                            disabled={index === 0}
                                            onClick={() => swap(index, index - 1)}
                                        >
                                            <AiOutlineArrowUp/>
                                        </ActionIcon>
                                        <ActionIcon
                                            aria-label={`Move step ${index + 1} down`}
                                            variant='subtle'
                                            disabled={index === steps.length - 1}
                                            onClick={() => swap(index, index + 1)}
                                        >
                                            <AiOutlineArrowDown/>
                                        </ActionIcon>
                                        <ActionIcon
                                            aria-label={`Remove step ${index + 1}`}
                                            variant='subtle'
                                            color='red'
                                            onClick={() => remove(index)}
                                        >
                                            <AiOutlineDelete/>
                                        </ActionIcon>
                                    </Group>
                                </Group>

                                <Autocomplete
                                    aria-label={`Step ${index + 1} action code`}
                                    label='Action Code'
                                    placeholder='user.login'
                                    data={activeActionCodes}
                                    value={step.actionCode}
                                    onChange={(value) =>
                                        form.setFieldValue(`steps.${index}.actionCode`, value)
                                    }
                                />

                                <Select
                                    aria-label={`Step ${index + 1} expected outcome`}
                                    label='Expected Outcome'
                                    data={EXPECTED_OUTCOME_OPTIONS}
                                    value={step.expect}
                                    allowDeselect={false}
                                    onChange={(value) =>
                                        form.setFieldValue(
                                            `steps.${index}.expect`,
                                            value ?? ExpectedOutcome.EXPECT_PASS,
                                        )
                                    }
                                />

                                <StepInputsField
                                    stepIndex={index}
                                    inputs={step.inputs ?? []}
                                />
                            </Stack>
                        </Card>
                    ))}

                    <Group>
                        <Button
                            variant='light'
                            leftSection={<AiOutlinePlus/>}
                            onClick={() => push({
                                                    // Provisional: the service renumbers from 1 on save.
                                                    stepNumber: steps.length + 1,
                                                    actionCode: '',
                                                    inputs: [],
                                                    expect: ExpectedOutcome.EXPECT_PASS,
                                                })}
                        >
                            Add Step
                        </Button>
                    </Group>
                </Stack>
            )}
        </FieldArray>
    );
};
